import { Button, Card, Col, Flex, Modal, Row } from "antd";
import React, { useMemo, useState } from "react";
import { DryerSettingsText } from "./DryerSettingsText";
import { SettingsTable } from "./SettingsTable";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import { formatNumber } from "src/utils/formatting";
import { getPremerPlusMachineTypeString, getPremierPlusFanTypeString, getPremierPlusLgsoModeString } from "src/utils/enumUtils";
import { convertFahrenheitToCelsius, convertFahrenheitToCelsiusOffset } from "src/utils/generalUtils";
import SweepOffTime from "src/consts/SweepOffTime";

interface DryerSettingsCardProps {
  binDto: PremierPlusBinDTO | null | undefined;
}
function getSweepOffTimeLabel(value: SweepOffTime | null | undefined): string {
  const options = [
    { label: "5", value: SweepOffTime.Five },
    { label: "10", value: SweepOffTime.Ten },
    { label: "15", value: SweepOffTime.Fifteen },
    { label: "20", value: SweepOffTime.Twenty },
    { label: "25", value: SweepOffTime.TwentyFive },
    { label: "30", value: SweepOffTime.Thirty },
    { label: "45", value: SweepOffTime.FourtyFive },
    { label: "60", value: SweepOffTime.Sixty },
    { label: "(10 - 60) Auto", value: SweepOffTime.TenToSixty },
    { label: "(20 - 60) Auto", value: SweepOffTime.TwentyToSixty },
    { label: "(30 - 60) Auto", value: SweepOffTime.ThirtyToSixty },
  ];

  const match = options.find((option) => option.value === value);
  return match ? match.label : "Unknown";
}

    
export const DryerSettingsCard: React.FC<DryerSettingsCardProps> = ({ binDto }) => {
  const [showTableSettings, setShowTableSettings] = useState(false);

  const dryingSettings = useMemo(
    () => binDto?.settings?.dryingOperationSettings,
    [binDto]
  );

  const useCelsius = useMemo(
    () => binDto?.settings?.generalSystemSettings?.useMetricSystem === true,
    [binDto]
  );

  const unit = useMemo(() => (useCelsius ? "°C" : "°F"), [useCelsius]);

  const toTemp = useMemo(
    () => (v?: number | null) =>
      v == null ? v : useCelsius ? convertFahrenheitToCelsius(v) : v,
    [useCelsius]
  );

  const toOffset = useMemo(
    () => (v?: number | null) =>
      v == null ? v : useCelsius ? convertFahrenheitToCelsiusOffset(v) : v,
    [useCelsius]
  );

  const targetTemp = useMemo(
    () => toTemp(dryingSettings?.targetPlenumTemperatureF),
    [dryingSettings, toTemp]
  );

  const maxTemp = useMemo(
    () => toTemp(dryingSettings?.maxPlenumTemperatureF),
    [dryingSettings, toTemp]
  );

  const minTemp = useMemo(
    () => toTemp(dryingSettings?.minPlenumTemperatureF),
    [dryingSettings, toTemp]
  );

  const plenumTempCal = useMemo(
    () => toOffset(dryingSettings?.plenumTempCalibrationF),
    [dryingSettings, toOffset]
  );

  const grainTempCal = useMemo(
    () => toOffset(dryingSettings?.grainTempCalibrationF),
    [dryingSettings, toOffset]
  );

  const text = useMemo(() => {
    const lines: string[] = [];

    lines.push(
      `Transfer Moisture=${formatNumber(dryingSettings?.targetMc, { decimalPlaces: 1, suffix: '%', filler: '--' })}`
    );

    lines.push(
      `Target Temp=${formatNumber(targetTemp, { decimalPlaces: 1, suffix: unit, filler: '--' })}`
    );

    binDto?.augers?.forEach((a, idx) => {
      lines.push(`Aug ${idx + 1} Delay=${a.cleanoutDelaySeconds}`);
    });

    lines.push(
      `Max Temp=${formatNumber(maxTemp, { decimalPlaces: 1, suffix: unit, filler: '--' })}`
    );
    lines.push(
      `Min Temp=${formatNumber(minTemp, { decimalPlaces: 1, suffix: unit, filler: '--' })}`
    );

    lines.push(
      `Plenum Temp Cal=${formatNumber(plenumTempCal, { decimalPlaces: 1, suffix: unit, filler: '--' })}`
    );
    lines.push(
      `Grain Temp Cal=${formatNumber(grainTempCal, { decimalPlaces: 1, suffix: unit, filler: '--' })}`
    );

    lines.push(
      `Moisture Cal=${formatNumber(dryingSettings?.grainMcCalibration, { decimalPlaces: 1, suffix: '%', filler: '--' })}`
    );
    lines.push(
      `Sweep Off Time=${getSweepOffTimeLabel(dryingSettings?.sweepOffTime)}`
    );
    lines.push(
      `Fan shutdown=${formatNumber(dryingSettings?.fanShutOffDelaySeconds, { decimalPlaces: 1, suffix: 'sec', filler: '--' })}`
    );

    lines.push(`Fan type=${getPremierPlusFanTypeString(binDto?.fanTypeId)}`);
    lines.push(`Check Moist Change=${dryingSettings?.disableMoistureChangingCheck === true ? 'No' : 'Yes'}`);
    lines.push(`Low grain shutdown=${getPremierPlusLgsoModeString(dryingSettings?.lgsoMode)}`);
    lines.push(`Bin Name=${binDto?.binName}`);
    lines.push(`Grain Type=${dryingSettings?.grainType}`);
    lines.push(`Number of samples in avg=${dryingSettings?.samplesCntInAverage}`);
    lines.push(`Machine type=${getPremerPlusMachineTypeString(binDto?.machineType)}`);
    lines.push(`Version=${binDto?.version}-${binDto?.hmiVersion}`);

    return lines.join("\r\n");
  }, [
    binDto,
    dryingSettings,
    targetTemp,
    maxTemp,
    minTemp,
    plenumTempCal,
    grainTempCal,
    unit,
  ]);

  return (
    <>
      <Card
        loading={false}
        title="Dryer Settings"
        style={{ width: "100%" }}
        styles={{
          body: {
            padding: 0,
            minWidth: "296px",
            width: "100%",
            minHeight: "504px",
          },
        }}
        extra={
          <Button onClick={() => setShowTableSettings(true)}>
            History
          </Button>
        }
      >
        <div style={{ width: "100%" }}>
          <Flex justify="center" style={{ width: "100%" }}>
            <DryerSettingsText text={text} />
          </Flex>
        </div>
      </Card>

      <Modal
        title="Dryer Settings History"
        styles={{ body: { overflowY: "auto", maxHeight: "calc(100vh - 200px)" } }}
        width={"70%"}
        footer={null}
        open={showTableSettings}
        onOk={() => setShowTableSettings(false)}
        onCancel={() => setShowTableSettings(false)}
      >
        <SettingsTable binId={binDto?.binId} />
      </Modal>
    </>
  );
};
